import type { FC } from 'react';
import { useIntl, FormattedPlural } from 'react-intl';

import type {
  xdlPaletteGrayCool04,
  xdlPalettePetrol03,
  xdlPalettePetrol05,
  xdlPalettePurpleRain03,
  xdlPalettePurpleRain05,
} from '@xing-com/tokens';
import { BodyCopy } from '@xing-com/typography';

import { getCompanyIndustryRoute } from '../../routes';
import type { IndustryItem } from '../../types';
import type { NwtTrackingProps } from '../../utils/tracking';
import { useNwtTrackingCompanies } from '../../utils/tracking';
import { IndustryCardSkeleton } from './industry-card.skeleton';
import * as Styled from './industry-card.styles';

export type IndustryCardBackground =
  | typeof xdlPaletteGrayCool04
  | typeof xdlPalettePetrol03
  | typeof xdlPalettePetrol05
  | typeof xdlPalettePurpleRain03
  | typeof xdlPalettePurpleRain05
  | '#F5AE87'
  | '#91D5C2';

type IndustryCardSubcomponents = {
  Skeleton: typeof IndustryCardSkeleton;
};

type IndustryCardProps = {
  item: IndustryItem;
  background?: IndustryCardBackground;
  trackingOptions?: NwtTrackingProps;
};

const defaultTrackingOptions = {
  element: 'industry_card',
};

export const IndustryCard: FC<IndustryCardProps> &
  IndustryCardSubcomponents = ({ item, trackingOptions }) => {
  const { $t } = useIntl();
  const trackNwtEvent = useNwtTrackingCompanies();

  const title = $t({ id: item.title });
  return (
    <Styled.Card
      background={item.background}
      to={getCompanyIndustryRoute(item.seoName, item.category)}
      aria-label={title}
      data-testid={`industryCard_${item.id}`}
      onClick={() =>
        trackingOptions &&
        trackNwtEvent({ ...defaultTrackingOptions, ...trackingOptions })
      }
    >
      <Styled.Headline size="large" sizeConfined="xxlarge">
        {title}
      </Styled.Headline>
      <BodyCopy noMargin>
        <FormattedPlural
          value={item.totalCompanies}
          one={$t({ id: 'INDUSTRY_CARD_TOTAL_COMPANIES.one' })}
          other={$t(
            { id: 'INDUSTRY_CARD_TOTAL_COMPANIES.other' },
            { amount: item.totalCompanies }
          )}
        />
      </BodyCopy>
    </Styled.Card>
  );
};

IndustryCard.Skeleton = IndustryCardSkeleton;
